import React from 'react';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { isEqual, isNil, isUndefined } from 'lodash-es';
import { PaymentMethod, StripeExpressCheckoutElementConfirmEvent } from '@stripe/stripe-js';
import { Elements, ExpressCheckoutElement, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';

import { CONFIGURATOR_DEFAULT_CURRENCY } from '@/components/modules/spark/configurator/constants';
import { Campaign } from '@/components/modules/spark/configurator/types';
import { getActiveCampaign } from '@/components/modules/spark/configurator/utils';
import PageLoader from '@/components/page-loader';
import { OWNERS_HUB_ROUTES } from '@/constants/routes';
import { useAppDispatch, useAppSelector } from '@/store';
import { sessionSave } from '@/store/slices/session';
import { getStripe, fetchInvoiceClientSecret } from '@/utils/stripe';
import { hasText, isEmailValid } from '@/utils/strings';

import { CheckoutFormProps, StripePaymentProps } from './types';

import './styles.scss';

const CheckoutForm: React.FC<CheckoutFormProps> = (props) => {
  const { clientSecret, paymentMethods, onFormReady, session } = props;

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const stripe = useStripe();
  const elements = useElements();

  const [emailErrorMessage, setEmailErrorMessage] = React.useState<string | undefined>();
  const [isExpressCheckoutReady, setIsExpressCheckoutReady] = React.useState(false);
  const [isPaymentReady, setIsPaymentReady] = React.useState(false);
  const [isProcessing, setIsProcessing] = React.useState<boolean>(false);
  const [errorMessage, setErrorMessage] = React.useState<string | undefined>();
  const [status, setStatus] = React.useState<'payment' | 'mail' | 'redirect' | 'success-mail' | 'success-redirect'>('payment');

  const hasSubmitted = React.useRef<boolean>(false);
  const emailTimeoutId = React.useRef(0);

  React.useEffect(() => {
    if (isExpressCheckoutReady && isPaymentReady) {
      onFormReady();
    }
  }, [isExpressCheckoutReady, isPaymentReady, onFormReady]);

  React.useEffect(() => {
    if (!isNil(session.postPayment) && status !== session.postPayment) {
      setIsProcessing(false);
      setStatus(session.postPayment);
    }
  }, [session.postPayment, status]);

  React.useEffect(() => {
    if (status === 'success-mail' || status === 'success-redirect') {
      navigate(OWNERS_HUB_ROUTES.CONFIGURATOR_PAYMENT_SUCCESSFUL);
    }
  }, [navigate, status]);

  const campaign = React.useMemo<Campaign | undefined>(() => {
    return getActiveCampaign(session.campaigns, session.data.configuration_data);
  }, [session]);

  const price = React.useMemo(() => {
    const { price_evaluation, currency_code = CONFIGURATOR_DEFAULT_CURRENCY } = campaign ?? {};
    const { match = false, discountedPrice = 0, basePrice = 0 } = price_evaluation ?? {};
    const finalPrice = match ? discountedPrice : basePrice;

    return `${finalPrice} ${currency_code}`;
  }, [campaign]);

  const handleEmailChange = React.useCallback<React.ChangeEventHandler<HTMLInputElement>>(
    (event) => {
      const emailAddress = event.target.value;

      if (hasSubmitted.current) {
        if (!hasText(emailAddress)) {
          setEmailErrorMessage('Email is required.');
        } else if (!isEmailValid(emailAddress)) {
          setEmailErrorMessage('Please enter a valid email address.');
        } else {
          setEmailErrorMessage(undefined);
        }
      }

      if (emailTimeoutId.current) {
        clearTimeout(emailTimeoutId.current);
      }

      emailTimeoutId.current = window.setTimeout(() => {
        if (!isNil(session.sessionId)) {
          dispatch(
            sessionSave({
              id: session.sessionId,
              data: {
                ...session.data,
                configuration_data: {
                  ...session.data.configuration_data,
                  emailAddress,
                },
              },
            }),
          );
        }
      }, 500);

      return () => {
        if (emailTimeoutId.current) {
          clearTimeout(emailTimeoutId.current);
        }
      };
    },
    [dispatch, session.data, session.sessionId],
  );

  const handleExpressPaymentConfirmation = React.useCallback(
    async (event: StripeExpressCheckoutElementConfirmEvent) => {
      if (isNil(stripe) || isNil(elements) || isUndefined(clientSecret)) {
        return;
      }

      if (!isNil(session.sessionId) && !isNil(event.billingDetails?.email)) {
        dispatch(
          sessionSave({
            id: session.sessionId,
            data: {
              ...session.data,
              configuration_data: {
                ...session.data.configuration_data,
                accountEmail: event.billingDetails.email,
              },
            },
          }),
        );

        setIsProcessing(true);
        setErrorMessage(undefined);

        const submitResult = await elements.submit();

        if (!isUndefined(submitResult.error)) {
          setIsProcessing(false);
          setErrorMessage(submitResult.error.message || 'An unknown error occurred');

          event.paymentFailed({
            reason: 'fail',
            message: submitResult.error.message || 'Payment failed, please try again.',
          });

          return;
        }

        const confirmPaymentResult = await stripe.confirmPayment({
          elements,
          clientSecret,
          confirmParams: {
            return_url: window.location.origin + OWNERS_HUB_ROUTES.STUDIO_CONFIGURATOR,
          },
          redirect: 'if_required',
        });

        if (!isUndefined(confirmPaymentResult.error)) {
          setIsProcessing(false);
          setErrorMessage(confirmPaymentResult.error.message || 'An unknown error occurred');

          event.paymentFailed({
            reason: 'fail',
            message: confirmPaymentResult.error.message || 'Payment failed, please try again.',
          });

          return;
        }

        if (!isUndefined(confirmPaymentResult.paymentIntent)) {
          // Nothing to do for now
        }
      }
    },
    [dispatch, stripe, elements, session.data, session.sessionId, clientSecret],
  );

  const handleSubmit = React.useCallback<React.FormEventHandler>(
    async (event) => {
      event.preventDefault();

      if (isNil(stripe) || isNil(elements) || isUndefined(clientSecret)) {
        return;
      }

      hasSubmitted.current = true;

      const currentEmailAddress = session.data.configuration_data?.emailAddress ?? '';

      let hasError = false;

      if (!hasText(currentEmailAddress)) {
        setEmailErrorMessage('Email is required.');

        hasError = true;
      } else if (!isEmailValid(currentEmailAddress)) {
        setEmailErrorMessage('Please enter a valid email address.');

        hasError = true;
      } else {
        setEmailErrorMessage(undefined);
      }

      if (hasError) {
        return;
      }

      setIsProcessing(true);
      setErrorMessage(undefined);

      const submitResult = await elements.submit();

      if (!isUndefined(submitResult.error)) {
        setIsProcessing(false);
        setErrorMessage(submitResult.error.message || 'An unknown error occurred');

        return;
      }

      const confirmPaymentResult = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: window.location.origin + OWNERS_HUB_ROUTES.STUDIO_CONFIGURATOR,
        },
        redirect: 'if_required',
      });

      if (!isUndefined(confirmPaymentResult.error)) {
        setIsProcessing(false);
        setErrorMessage(confirmPaymentResult.error.message || 'An unknown error occurred');

        return;
      }

      if (!isUndefined(confirmPaymentResult.paymentIntent)) {
        // Nothing to do for now
      }
    },
    [stripe, elements, clientSecret, session.data],
  );

  const handleRedirect = React.useCallback(() => {
    navigate(OWNERS_HUB_ROUTES.STUDIO_CONFIGURATOR);
  }, [navigate]);

  switch (status) {
    case 'payment':
      return (
        <>
          <div className={'section section-contact'}>
            <div className={'section-header'}>Contact info</div>
            <div className={'section-body'}>
              <label htmlFor={'payment-emailInput'} className={'email-label'}>
                Email
              </label>
              <input
                type={'email'}
                id={'payment-emailInput'}
                className={classNames('email-input', { error: !isUndefined(emailErrorMessage) })}
                placeholder={'john@mail.com'}
                value={session.data.configuration_data?.emailAddress ?? ''}
                onChange={handleEmailChange}
              />
              {!isUndefined(emailErrorMessage) ? <div className={'email-error'}>{emailErrorMessage}</div> : null}
            </div>
          </div>
          <div className={'section section-payment'}>
            <div className={'section-header'}>Choose payment method</div>
            <div className={'section-subheader'}>Express checkout</div>
            <div className={'section-body'}>
              <ExpressCheckoutElement
                onReady={() => setIsExpressCheckoutReady(true)}
                onConfirm={handleExpressPaymentConfirmation}
              />
            </div>
            <div className={'section-subheader'}>Other payment methods</div>
            <form onSubmit={handleSubmit} className={'payment-form'}>
              <div className={'section-body'}>
                <PaymentElement
                  onReady={() => setIsPaymentReady(true)}
                  onChange={() => setErrorMessage(undefined)}
                  options={{
                    layout: {
                      type: 'accordion',
                      // Change to true, if we want all payment methods to be collapsed by default
                      defaultCollapsed: false,
                    },
                  }}
                />
                {!isUndefined(errorMessage) ? (
                  <div className={'error-message'}>
                    <i className={'bi bi-exclamation-triangle-fill'}></i>
                    <div className={'text'}>{errorMessage}</div>
                  </div>
                ) : null}
              </div>
              <div className={'footer'}>
                <div className={'live-payment'}>
                  <div className={'details'}>
                    <div className={'title'}>Order total</div>
                  </div>
                  <div className={'payments'}>
                    <div className={'prices'}>
                      <div className={'current'}>{price}</div>
                    </div>
                  </div>
                </div>
                <button disabled={!stripe || isProcessing} className={'submit-btn'}>
                  {isProcessing ? 'Processing...' : 'Pay for your brand'}
                </button>
              </div>
            </form>
          </div>
        </>
      );

    case 'mail':
      return (
        <>
          <div className={'section section-confirmation'}>
            <div className={'payment-status'}>
              <div className={'status-icon'}>
                <i className={'bi bi-check-circle'}></i>
              </div>
              <div className={'status-text'}>Payment successful.</div>
              <div className={'status-details'}>Please check your mail in order to continue the process.</div>
            </div>
          </div>
        </>
      );

    case 'redirect':
      return (
        <>
          <div className={'section section-confirmation'}>
            <div className={'payment-status'}>
              <div className={'status-icon'}>
                <i className={'bi bi-check-circle'}></i>
              </div>
              <div className={'status-text'}>Payment successful</div>
            </div>
            <div className={'submit-btn'} onClick={handleRedirect}>
              Continue <i className={'bi bi-arrow-right'}></i>
            </div>
          </div>
        </>
      );

    default:
      return null;
  }
};

export const StripePayment: React.FC<StripePaymentProps> = () => {
  const session = useAppSelector((state) => state.session, isEqual);

  const [clientSecret, setClientSecret] = React.useState<string | undefined>();
  const [paymentMethods, setPaymentMethods] = React.useState<PaymentMethod[]>([]);

  const [isDataReady, setIsDataReady] = React.useState(false);
  const [isFormReady, setIsFormReady] = React.useState(false);

  React.useEffect(() => {
    if (!isNil(session.sessionId)) {
      // payment-methods API is very slow, so we only fetch the invoice clientSecret.
      // (For payment methods, see git history for the `Promise.all` variant.)
      fetchInvoiceClientSecret(session.sessionId)
        .then((secret) => {
          setClientSecret(secret);
          setIsDataReady(true);
        })
        // fetchInvoiceClientSecret rejects on network error or a missing
        // provider_reference; log it instead of leaving an unhandled rejection
        // (the loader stays up, matching the prior behaviour).
        .catch((error) => {
          console.error('[Stripe] Error fetching invoice clientSecret:', error);
        });
    }
  }, [session.sessionId]);

  const isLoading = !isDataReady || !isFormReady;

  return (
    <div className={'stripe-payment-container'}>
      {isLoading ? <PageLoader backgroundColor={'#101014'} fullscreen={false} /> : null}
      {!isUndefined(clientSecret) ? (
        <Elements
          stripe={getStripe()}
          options={{
            clientSecret,
            appearance: {
              theme: 'night',
              labels: 'above',
              variables: {
                colorBackground: '#18181B',
                colorText: '#D4D4D8',
                colorPrimary: '#FF791B',
                colorTextSecondary: '#D4D4D8',
                colorTextPlaceholder: '#D4D4D8',
                borderRadius: '12px',
                fontFamily: 'Satoshi, sans-serif',
                colorDanger: '#F00',
                colorSuccess: '#0F0',
              },
              rules: {
                '.AccordionItem': {
                  boxShadow: 'none',
                  borderColor: '#27272A',
                  backgroundColor: '#18181B',
                },
                '.AccordionItem--selected': {
                  boxShadow: 'none',
                },
                '.AccordionItem:hover': {
                  boxShadow: 'none',
                },
                '.AccordionItem:focus-visible': {
                  boxShadow: 'none',
                },
                '.Label': {
                  color: '#A1A1AA',
                  fontSize: '12px',
                  lineHeight: '110%',
                  marginBottom: '6px',
                },
                '.Input': {
                  boxShadow: 'none',
                  borderColor: '#1E1E20',
                  backgroundColor: '#1E1E20',
                  fontSize: '12px',
                  lineHeight: '16px',
                  padding: '6px 12px',
                },
                '.Input--empty': {
                  boxShadow: 'none',
                },
                '.Input--invalid': {
                  boxShadow: 'none',
                },
                '.Input:hover': {
                  boxShadow: 'none',
                },
                '.Input:focus': {
                  boxShadow: 'none',
                },
                '.Input:disabled': {
                  boxShadow: 'none',
                },
                '.Input:autofill': {
                  boxShadow: 'none',
                },
                '.Input::placeholder': {
                  boxShadow: 'none',
                  color: '#52525B',
                  fontSize: '12px',
                  lineHeight: '16px',
                },
                '.Input::selection': {
                  boxShadow: 'none',
                  color: '#52525B',
                  fontSize: '12px',
                  lineHeight: '16px',
                },
                '.Error': {
                  fontSize: '12px',
                  lineHeight: '110%',
                  marginTop: '6px',
                },
                '.TermsText': {
                  color: '#A1A1AA',
                  fontSize: '12px',
                  lineHeight: '110%',
                },
              },
            },
          }}
        >
          <CheckoutForm
            clientSecret={clientSecret}
            paymentMethods={paymentMethods}
            onFormReady={() => setIsFormReady(true)}
            session={session}
          />
        </Elements>
      ) : null}
    </div>
  );
};

export default StripePayment;
